import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { Slot } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Spinner } from './Spinner';

/**
 * Przycisk (Figma: Button 3004:259). Primary = jedna główna akcja na widok, Secondary = pomocnicze,
 * Ghost = akcje w paskach i listach. Rozmiary: M 32 px, S 28 px; L 40 px tylko w formularzach logowania i na mobile.
 *
 * Lifting 17.10.2026 (DECISIONS „Lekki lifting UI”, DESIGN „Lifting 17.10.2026”): Ghost ma lekkie tło `soft`
 * (półprzezroczyste — widać je i na białej sekcji, i na szarym tle), a nie sam tekst; Secondary — wyraźniejszą ramkę
 * i tło przy najechaniu. Primary bez zmian. Nazwy wariantów i API bez zmian.
 */
export const buttonVariants = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap select-none',
    'transition-[background-color,border-color,color,box-shadow,opacity] duration-100',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    'disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-hover',
        secondary:
          'border border-line bg-canvas text-fg shadow-xs hover:border-line-strong hover:bg-elevated active:bg-elevated',
        ghost: 'bg-soft text-fg-secondary hover:bg-soft-hover hover:text-fg active:bg-soft-active',
      },
      size: {
        s: 'h-7 px-2.5 text-caption',
        m: 'h-8 px-3 text-label',
        l: 'h-10 px-4 text-label',
      },
      iconOnly: { true: 'px-0', false: '' },
      fullWidth: { true: 'w-full', false: '' },
    },
    compoundVariants: [
      // sama ikona (np. ⋯ w wierszu listy): bez tła do najechania, żeby wiersze nie zamieniły się w rząd kafelków
      { iconOnly: true, variant: 'ghost', className: 'bg-transparent' },
      { iconOnly: true, size: 's', className: 'w-7' },
      { iconOnly: true, size: 'm', className: 'w-8' },
      { iconOnly: true, size: 'l', className: 'w-10' },
    ],
    defaultVariants: { variant: 'secondary', size: 'm', iconOnly: false, fullWidth: false },
  },
);

const ICON_SIZE = { s: 14, m: 16, l: 16 } as const;

export interface ButtonProps
  extends Omit<ComponentProps<'button'>, 'children'>,
    Omit<VariantProps<typeof buttonVariants>, 'iconOnly'> {
  icon?: LucideIcon;
  iconRight?: LucideIcon;
  loading?: boolean;
  /** Renderuje dziecko (np. <Link>) z wyglądem przycisku. */
  asChild?: boolean;
  children?: ReactNode;
  /** Wymagane, gdy przycisk ma tylko ikonę. */
  'aria-label'?: string;
}

export function Button({
  variant,
  size,
  fullWidth,
  icon: Icon,
  iconRight: IconRight,
  loading = false,
  asChild = false,
  className,
  children,
  disabled,
  type,
  ...props
}: ButtonProps) {
  const iconSize = ICON_SIZE[size ?? 'm'];
  const iconOnly = !children && Boolean(Icon);
  const classes = cn(buttonVariants({ variant, size, fullWidth, iconOnly }), className);

  if (asChild) {
    return (
      <Slot.Root className={classes} {...props}>
        {children}
      </Slot.Root>
    );
  }

  return (
    <button
      type={type ?? 'button'}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <Spinner size={iconSize} />
      ) : (
        Icon && <Icon size={iconSize} strokeWidth={1.75} aria-hidden className="shrink-0" />
      )}
      {children}
      {IconRight && !loading && <IconRight size={iconSize} strokeWidth={1.75} aria-hidden className="shrink-0" />}
    </button>
  );
}

/** Kwadratowy przycisk z ikoną 28 px (Figma: Icon button w Sidebar v2). */
export function IconButton({
  icon: Icon,
  label,
  className,
  raised = false,
  size = 28,
  iconSize = 17,
  type,
  ...props
}: Omit<ComponentProps<'button'>, 'children'> & {
  icon: LucideIcon;
  label: string;
  /** Biały kafelek z cieniem (Dodaj w nagłówku menu). */
  raised?: boolean;
  size?: 28 | 32 | 36;
  iconSize?: number;
}) {
  return (
    <button
      type={type ?? 'button'}
      aria-label={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md text-fg-secondary transition-colors',
        'hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        'disabled:pointer-events-none disabled:opacity-50',
        raised && 'bg-canvas text-fg shadow-xs hover:bg-canvas hover:text-fg',
        size === 28 && 'size-7',
        size === 32 && 'size-8',
        size === 36 && 'size-9',
        className,
      )}
      {...props}
    >
      <Icon size={iconSize} strokeWidth={1.75} aria-hidden />
    </button>
  );
}
